import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Action, State, StateContext } from '@ngxs/store';
import { MessageService, TreeNode } from 'primeng/api';
import { catchError, EMPTY, filter, map, Observable, of, tap } from 'rxjs';

import { GlobalSiteMasterStoreService } from '@customer-portal/data-access/global';
import { DEFAULT_GRID_CONFIG } from '@customer-portal/shared';
import { getToastContentBySeverity } from '@customer-portal/shared/helpers/custom-toast';
import { getFilterOptions } from '@customer-portal/shared/helpers/grid';
import {
  CoBrowsingCompany,
  ENGLISH_LANGUAGE_CODE,
  FilterableColumnDefinition,
  Language,
  ToastSeverity,
  ToastSeverityIcons,
} from '@customer-portal/shared/models';
import { FilterOptions, GridConfig } from '@customer-portal/shared/models/grid';
import { CoBrowsingSharedService } from '@customer-portal/shared/services';
import { CoBrowsingCookieService } from '@customer-portal/shared/services/co-browsing';
import { LocaleService } from '@customer-portal/shared/services/locale';

import {
  CoBrowsingCompanyListDto,
  ProfileLanguageDto,
  SettingsAdminListDto,
  SettingsCompanyDetailsCountryListDataDto,
  SettingsCompanyDetailsCountryListDto,
  SettingsMembersListDto,
  SettingsMembersPermissionsDto,
  SettingsMembersRolesDto,
} from '../dtos';
import {
  AccessAreaRoleNames,
  CreateContactAccessArea,
  CreateContactRequest,
  ManageMembersRequestModel,
  MemberAreaPermissions,
  MemberAreasPermissions,
  ProfileInformationModel,
  ProfileSettingsSelection,
  SelectedUserDetailsModel,
  SettingsCompanyDetailsCountryListData,
  SettingsCompanyDetailsData,
  SettingsMembersItemModel,
  SettingsMembersPermissionsDataModel,
  SettingsNewMemberFormModel,
  UserDetailsToManagePermission,
} from '../models';
import {
  CoBrowsingCompanyListService,
  CoBrowsingMapperService,
  ProfileLanguageMapperService,
  ProfileLanguageService,
  ProfileMapperService,
  ProfileService,
  SettingsAdminListService,
  SettingsCommunicationLanguageService,
  SettingsCompanyDetailsMapperService,
  SettingsCompanyDetailsService,
  SettingsMembersListService,
  SettingsMembersMapper,
  SettingsMembersPermissionsService,
  SettingsMembersRolesService,
  SettingsUserDetailsToManagePermission,
} from '../services';
import {
  ChangeEditManagePermissionsPristineForm,
  DiscardMemberPermissionsDataAndCompanies,
  DiscardMemberPermissionsUserSelection,
  DiscardNewMemberFormInfo,
  GenerateMemberPermissionsServicesOptions,
  GenerateMemberPermissionsSitesOptions,
  GetCompanySuggestions,
  GetCompanySuggestionsError,
  GetCompanySuggestionsSuccess,
  LoadMemberRoles,
  LoadMemberRolesSuccess,
  LoadMembersPermissions,
  LoadMembersPermissionsCompanies,
  LoadMembersPermissionsServices,
  LoadMembersPermissionsSites,
  LoadMembersPermissionsSuccess,
  LoadProfileData,
  LoadSettingsAdminList,
  LoadSettingsAdminListFail,
  LoadSettingsAdminListSuccess,
  LoadSettingsCompanyDetails,
  LoadSettingsCompanyDetailsCountryList,
  LoadSettingsCompanyDetailsCountryListSuccess,
  LoadSettingsCompanyDetailsFail,
  LoadSettingsCompanyDetailsSuccess,
  LoadSettingsMembersList,
  LoadSettingsMembersListFail,
  LoadSettingsMembersListSuccess,
  LoadUserDetailsToManagePermission,
  LoadUserDetailsToManagePermissionSuccess,
  LoadUserRoles,
  LoadUserRolesSuccess,
  RemoveMember,
  RemoveMemberError,
  RemoveMemberSuccess,
  ResetAdminListState,
  ResetCompanyLoadAndErrorState,
  ResetMembersListState,
  ResetProfileLoadAndErrorState,
  ResetSelectedCobrowsingCompany,
  ResetSettingsCompanyDetailsState,
  SaveMemberPermissionsCompanies,
  SaveMemberPermissionsServices,
  SaveMemberPermissionsSites,
  SendWelcomeEmail,
  SendWelcomeEmailError,
  SendWelcomeEmailSuccess,
  SetCompanyDetailsAdminStatus,
  SetProfileLanguage,
  SetUserLastLoginTime,
  SubmitManageMembersPermissions,
  SubmitManageMembersPermissionsError,
  SubmitManageMembersPermissionsSuccess,
  SubmitNewMemberInfo,
  SubmitNewMemberInfoError,
  SubmitNewMemberInfoSuccess,
  SwitchContinueToPermissionsStatus,
  SwitchHasMemberAdminPermissions,
  SwitchMemberPermissionsServicesDropdownAccess,
  SwitchMemberPermissionsSitesDropdownAccess,
  UpdateAdminFilterOptions,
  UpdateAdminGridConfig,
  UpdateCommunicationLanguage,
  UpdateCommunicationLanguageError,
  UpdateCommunicationLanguageSuccess,
  UpdateDnvUserAccessType,
  UpdateEditCompanyDetailsFormValidity,
  UpdateImpersonatedUser,
  UpdateIsDnvUser,
  UpdateMemberAreasPermissions,
  UpdateMembersFilterOptions,
  UpdateMembersGridConfig,
  UpdateNewMemberForm,
  UpdateProfileLanguage,
  UpdateProfileLanguageError,
  UpdateProfileLanguageSuccess,
  UpdateSelectedCobrowsingCompany,
  UpdateSettingsCompanyDetails,
  UpdateSettingsCompanyDetailsEntityListFilterOptions,
  UpdateSettingsCompanyDetailsEntityListGridConfig,
  UpdateSubmitSettingsStateValues,
  UpdateSubmitSettingsStatus,
  UpdateSubmitSettingsValues,
  UpdateUserLastLoginTime,
} from './actions';

interface LoadingErrorState {
  profileData: string | null;
  profileLanguage: string | null;
  companyDetails: string | null;
  membersList: string | null;
  adminList: string | null;
}

export interface SettingsStateModel {
  isUserAdmin: boolean;
  userLastLoginTime: string;
  parentCompany: SettingsCompanyDetailsData | null;
  legalEntityList: SettingsCompanyDetailsData[];
  legalEntityListCounter: number;
  legalEntityGridConfig: GridConfig;
  legalEntityFilterOptions: FilterOptions;
  countryList: SettingsCompanyDetailsCountryListData[];
  countryActiveId: number | null;
  adminList: SettingsMembersItemModel[];
  adminGridConfig: GridConfig;
  adminFilterOptions: FilterOptions;
  membersList: SettingsMembersItemModel[];
  membersGridConfig: GridConfig;
  membersFilterOptions: FilterOptions;
  information: ProfileInformationModel;
  userSelection: ProfileSettingsSelection;
  submitSettingsStatus: boolean;
  memberRoles: string[];
  userRoles: string[];
  isAddMemberFormValid: boolean;
  newMemberForm: SettingsNewMemberFormModel | null;
  permissionsData: SettingsMembersPermissionsDataModel | null;
  companies: TreeNode[];
  sites: TreeNode[];
  services: TreeNode[];
  hasReceivedAdminPermissions: boolean;
  sitesDropdownDisabled: boolean;
  servicesDropdownDisabled: boolean;
  selectedCompanyIds?: number[];
  selectedServiceIds?: number[];
  selectedSiteIds?: (string | number)[];
  memberAreasPermissions?: MemberAreasPermissions[];
  isEditCompanyDetailsFormValid: boolean;
  userDetailsToManagePermission: UserDetailsToManagePermission | null;
  selectedUserDetails: SelectedUserDetailsModel | null;
  isEditManagePermissionsFormPristine: boolean;
  isDnvUser: boolean;

  companySuggestion: {
    suggestionList: CoBrowsingCompany[];
    isLoading: boolean;
    error: string;
  };

  selectedCoBrowsingCompany: CoBrowsingCompany | null;
  selectedCoBrowsingCompanyId: string | null;

  loadingErrors: LoadingErrorState;
  error?: string;
  loadedStates: {
    profileData: boolean;
    profileLanguage: boolean;
    companyDetails: boolean;
    membersList: boolean;
    adminList: boolean;
  };
  addEditUserPermission: {
    isLoading: boolean;
    error: string | null;
  };
}

const defaultState: SettingsStateModel = {
  isUserAdmin: false,
  userLastLoginTime: '',
  parentCompany: null,
  legalEntityList: [],
  legalEntityListCounter: 0,
  legalEntityGridConfig: DEFAULT_GRID_CONFIG,
  legalEntityFilterOptions: {},
  countryList: [],
  countryActiveId: null,
  adminList: [],
  adminGridConfig: DEFAULT_GRID_CONFIG,
  adminFilterOptions: {},
  membersList: [],
  membersGridConfig: DEFAULT_GRID_CONFIG,
  membersFilterOptions: {},
  information: {
    firstName: '',
    lastName: '',
    country: '',
    countryCode: '',
    region: '',
    email: '',
    phone: '',
    portalLanguage: ENGLISH_LANGUAGE_CODE,
    veracityId: '',
    communicationLanguage: ENGLISH_LANGUAGE_CODE,
    oscContactId: '',
    title: '',
    jobTitle: '',
    accessLevel: {},
    sidebarMenu: [],
  },
  userSelection: { jobTitle: '' },
  submitSettingsStatus: false,
  memberRoles: [],
  userRoles: [],
  isAddMemberFormValid: false,
  newMemberForm: null,
  permissionsData: null,
  companies: [],
  services: [],
  sites: [],
  hasReceivedAdminPermissions: true, // By default admin permissions are granted
  sitesDropdownDisabled: true,
  servicesDropdownDisabled: true,
  isEditCompanyDetailsFormValid: true,
  userDetailsToManagePermission: null,
  selectedUserDetails: null,
  isEditManagePermissionsFormPristine: true,
  isDnvUser: false,
  companySuggestion: {
    suggestionList: [],
    isLoading: false,
    error: '',
  },

  selectedCoBrowsingCompany: null,
  selectedCoBrowsingCompanyId: null,
  loadingErrors: {
    profileData: null,
    profileLanguage: null,
    companyDetails: null,
    membersList: null,
    adminList: null,
  },
  loadedStates: {
    profileData: false,
    profileLanguage: false,
    companyDetails: false,
    membersList: false,
    adminList: false,
  },
  addEditUserPermission: {
    isLoading: false,
    error: '',
  },
};

const COLUMNS_DEFINITION: FilterableColumnDefinition[] = [
  { field: 'name', hasColumnDelimiter: false },
  { field: 'email', hasColumnDelimiter: false },
  { field: 'company', hasColumnDelimiter: true },
  { field: 'services', hasColumnDelimiter: true },
  { field: 'sites', hasColumnDelimiter: true },
  { field: 'access', hasColumnDelimiter: false },
  { field: 'lastLoginTime', hasColumnDelimiter: false },
];

const COMPANY_DETAILS_LEGAL_ENTITIES_COLUMNS_DEFINITION: FilterableColumnDefinition[] =
  [{ field: 'organizationName', hasColumnDelimiter: false }];

@State<SettingsStateModel>({
  name: 'settings',
  defaults: defaultState,
})
@Injectable()
export class SettingsState {
  private readonly ts = inject(TranslocoService);
  private readonly globalSiteMasterStoreService = inject(
    GlobalSiteMasterStoreService,
  );

  constructor(
    private readonly profileService: ProfileService,
    private readonly messageService: MessageService,
    private readonly settingsCompanyDetailsService: SettingsCompanyDetailsService,
    private readonly settingsMembersListService: SettingsMembersListService,
    private readonly settingsAdminListService: SettingsAdminListService,
    private readonly settingsCommunicationLanguageService: SettingsCommunicationLanguageService,
    private readonly localeService: LocaleService,
    private readonly profileLanguageService: ProfileLanguageService,
    private readonly translocoService: TranslocoService,
    private readonly settingsMembersRolesService: SettingsMembersRolesService,
    private readonly settingsMembersPermissionsService: SettingsMembersPermissionsService,
    private readonly settingsUserDetailsToManagePermission: SettingsUserDetailsToManagePermission,
    private readonly coBrowsingCompanyListService: CoBrowsingCompanyListService,
    private readonly coBrowsingCookieService: CoBrowsingCookieService,
    public readonly coBrowsingSharedService: CoBrowsingSharedService,
  ) {}

  // #region InitialLogin

  @Action(SetUserLastLoginTime)
  setUserLastLoginTime(
    ctx: StateContext<SettingsStateModel>,
    { userLastLoginTime }: SetUserLastLoginTime,
  ) {
    ctx.patchState({
      userLastLoginTime,
    });
  }

  // #endregion InitialLogin

  // #region SettingsMembers

  @Action(LoadSettingsAdminList)
  loadSettingsAdminList(ctx: StateContext<SettingsStateModel>) {
    const accountDnvId = ctx.getState().selectedCoBrowsingCompanyId;
    const cobrowsingUserEmail =
      this.coBrowsingSharedService.getCoBrowsingUserEmail();
    const { accessType } = ctx.getState().information;

    ctx.patchState({
      loadedStates: {
        ...ctx.getState().loadedStates,
        adminList: false,
      },
      loadingErrors: {
        ...ctx.getState().loadingErrors,
        adminList: null,
      },
    });

    return this.settingsAdminListService
      .getSettingsAdminList(accountDnvId)
      .pipe(
        tap((data: SettingsAdminListDto) => {
          const adminList = SettingsMembersMapper.mapToAdminList(
            data,
            accountDnvId,
            cobrowsingUserEmail,
            accessType,
          );
          ctx.dispatch(new LoadSettingsAdminListSuccess(adminList));
          ctx.dispatch(new UpdateAdminFilterOptions());
        }),
        catchError((error) => {
          const errorMessage = error?.message || 'Failed to load admin list';
          ctx.dispatch(new LoadSettingsAdminListFail(errorMessage));

          return of(null);
        }),
      );
  }

  @Action(LoadSettingsAdminListSuccess)
  loadSettingsAdminListSuccess(
    ctx: StateContext<SettingsStateModel>,
    { adminList }: LoadSettingsAdminListSuccess,
  ) {
    ctx.patchState({
      adminList,
      loadedStates: {
        ...ctx.getState().loadedStates,
        adminList: true,
      },
    });
  }

  @Action(LoadSettingsAdminListFail)
  loadSettingsAdminListFail(
    ctx: StateContext<SettingsStateModel>,
    { errorMessage }: LoadSettingsAdminListFail,
  ) {
    ctx.patchState({
      loadingErrors: {
        ...ctx.getState().loadingErrors,
        adminList: errorMessage,
      },
      loadedStates: {
        ...ctx.getState().loadedStates,
        adminList: true,
      },
    });
  }

  @Action(LoadSettingsMembersList)
  loadSettingsMembersList(ctx: StateContext<SettingsStateModel>) {
    const accountDnvId = ctx.getState().selectedCoBrowsingCompanyId;
    const cobrowsingUserEmail =
      this.coBrowsingSharedService.getCoBrowsingUserEmail();
    const { accessType } = ctx.getState().information;

    ctx.patchState({
      loadedStates: {
        ...ctx.getState().loadedStates,
        membersList: false,
      },
      loadingErrors: {
        ...ctx.getState().loadingErrors,
        membersList: null,
      },
    });

    return this.settingsMembersListService
      .getSettingsMembersList(accountDnvId)
      .pipe(
        tap((data: SettingsMembersListDto) => {
          const membersList = SettingsMembersMapper.mapToMembersList(
            data,
            accountDnvId,
            cobrowsingUserEmail,
            accessType,
          );
          ctx.dispatch(new LoadSettingsMembersListSuccess(membersList));
          ctx.dispatch(new UpdateMembersFilterOptions());
        }),
        catchError((error) => {
          const errorMessage = error?.message || 'Failed to load members list';
          ctx.dispatch(new LoadSettingsMembersListFail(errorMessage));

          return of(null);
        }),
      );
  }

  @Action(LoadSettingsMembersListSuccess)
  loadSettingsMembersListSuccess(
    ctx: StateContext<SettingsStateModel>,
    { membersList }: LoadSettingsMembersListSuccess,
  ) {
    ctx.patchState({
      membersList,
      loadedStates: {
        ...ctx.getState().loadedStates,
        membersList: true,
      },
    });
  }

  @Action(LoadSettingsMembersListFail)
  loadSettingsMembersListFail(
    ctx: StateContext<SettingsStateModel>,
    { errorMessage }: LoadSettingsMembersListFail,
  ) {
    ctx.patchState({
      loadingErrors: {
        ...ctx.getState().loadingErrors,
        membersList: errorMessage,
      },
      loadedStates: {
        ...ctx.getState().loadedStates,
        membersList: true,
      },
    });
  }

  @Action(UpdateMembersGridConfig)
  updateMembersGridConfig(
    ctx: StateContext<SettingsStateModel>,
    { membersGridConfig }: UpdateMembersGridConfig,
  ): void {
    ctx.patchState({ membersGridConfig });
    ctx.dispatch(new UpdateMembersFilterOptions());
  }

  @Action(UpdateMembersFilterOptions)
  updateMembersFilterOptions(ctx: StateContext<SettingsStateModel>): void {
    const { membersGridConfig, membersList } = ctx.getState();

    const columnFilterConfigs: FilterableColumnDefinition[] =
      COLUMNS_DEFINITION;

    const filterOptions = getFilterOptions(
      membersList,
      membersGridConfig,
      columnFilterConfigs,
    );

    ctx.patchState({ membersFilterOptions: filterOptions });
  }

  @Action(ResetMembersListState)
  resetMembersListState(ctx: StateContext<SettingsStateModel>): void {
    ctx.patchState({
      membersList: [],
      membersGridConfig: DEFAULT_GRID_CONFIG,
      membersFilterOptions: {},
    });
  }

  @Action(UpdateAdminGridConfig)
  updateAdminGridConfig(
    ctx: StateContext<SettingsStateModel>,
    { adminGridConfig }: UpdateAdminGridConfig,
  ): void {
    ctx.patchState({ adminGridConfig });
    ctx.dispatch(new UpdateAdminFilterOptions());
  }

  @Action(UpdateAdminFilterOptions)
  updateAdminFilterOptions(ctx: StateContext<SettingsStateModel>): void {
    const { adminGridConfig, adminList } = ctx.getState();

    const columnFilterConfigs: FilterableColumnDefinition[] =
      COLUMNS_DEFINITION;

    const filterOptions = getFilterOptions(
      adminList,
      adminGridConfig,
      columnFilterConfigs,
    );

    ctx.patchState({ adminFilterOptions: filterOptions });
  }

  @Action(ResetAdminListState)
  resetAdminListState(ctx: StateContext<SettingsStateModel>): void {
    ctx.patchState({
      adminList: [],
      adminGridConfig: DEFAULT_GRID_CONFIG,
      adminFilterOptions: {},
    });
  }

  @Action(LoadUserDetailsToManagePermission, { cancelUncompleted: true })
  loadUserDetailsToManagePermission(
    ctx: StateContext<SettingsStateModel>,
    { memberEmail, skipLoadingOverlay }: LoadUserDetailsToManagePermission,
  ) {
    const accountDnvId = ctx.getState().selectedCoBrowsingCompanyId;
    ctx.patchState({
      addEditUserPermission: {
        isLoading: true,
        error: null,
      },
    });

    return this.settingsUserDetailsToManagePermission
      .getUserDetailsToManagePermission(
        memberEmail,
        accountDnvId,
        skipLoadingOverlay,
      )
      .pipe(
        tap((response) => {
          const userDetails =
            Array.isArray(response.data) && response.data.length > 0
              ? response.data[0]
              : undefined;

          if (!userDetails) {
            throw new Error('User details not found in response');
          }

          ctx.patchState({
            selectedUserDetails: {
              email: userDetails.emailId,
              firstName: userDetails.firstName,
              lastName: userDetails.lastName,
              jobTitle: userDetails.jobTitle,
            },
          });
          ctx.dispatch(new LoadUserDetailsToManagePermissionSuccess(response));
        }),
        catchError((err) => {
          ctx.patchState({
            addEditUserPermission: {
              isLoading: false,
              error: err.message,
            },
          });
          this.messageService.add({
            severity: 'error',
            summary: this.ts.translate('toast.somethingWentWrong'),
            icon: ToastSeverityIcons.ExclamationCircle,
          });

          return EMPTY;
        }),
      );
  }

  @Action(LoadUserDetailsToManagePermissionSuccess)
  loadUserDetailsToManagePermissionSuccess(
    ctx: StateContext<SettingsStateModel>,
    { permissionsData }: LoadUserDetailsToManagePermissionSuccess,
  ) {
    ctx.patchState({
      permissionsData,
      addEditUserPermission: {
        isLoading: false,
        error: null,
      },
    });
    const { hasReceivedAdminPermissions } = ctx.getState();
    const companies =
      SettingsMembersMapper.mapToMemberCompanies(permissionsData);

    ctx.dispatch(new LoadMembersPermissionsCompanies(companies));

    const mapToMemberAreasPermissions =
      SettingsMembersMapper.mapAccessAreasToMemberPermissions(
        permissionsData.data[0].accessArea,
      );

    ctx.dispatch(
      new UpdateMemberAreasPermissions(
        hasReceivedAdminPermissions
          ? this.getAdminMemberPermissions()
          : mapToMemberAreasPermissions,
      ),
    );
  }
  // #endregion SettingsMembers

  // #region SettingsMembersPermissions

  @Action(LoadMemberRoles)
  LoadMemberRoles(ctx: StateContext<SettingsStateModel>) {
    return this.settingsMembersRolesService.getSettingsMembersRoles().pipe(
      tap((data: SettingsMembersRolesDto) => {
        if (data.isSuccess) {
          ctx.dispatch(new LoadMemberRolesSuccess(data.data));
        }
      }),
    );
  }

  @Action(LoadMemberRolesSuccess)
  LoadMemberRolesSuccess(
    ctx: StateContext<SettingsStateModel>,
    { roles }: LoadMemberRolesSuccess,
  ) {
    ctx.patchState({ memberRoles: roles });
  }

  @Action(SwitchContinueToPermissionsStatus)
  switchContinueToPermissionsStatus(
    ctx: StateContext<SettingsStateModel>,
    { isAddMemberFormValid }: SwitchContinueToPermissionsStatus,
  ) {
    ctx.patchState({
      isAddMemberFormValid,
    });
  }

  @Action(UpdateNewMemberForm)
  updateNewMemberForm(
    ctx: StateContext<SettingsStateModel>,
    { newMemberForm }: UpdateNewMemberForm,
  ) {
    ctx.patchState({
      newMemberForm,
    });
  }

  @Action(LoadMembersPermissions)
  loadMembersPermissions(ctx: StateContext<SettingsStateModel>) {
    const memberEmail = ctx.getState().newMemberForm!.email;
    const accountDnvId = ctx.getState().selectedCoBrowsingCompanyId;

    ctx.patchState({
      addEditUserPermission: {
        isLoading: true,
        error: null,
      },
    });

    return this.settingsMembersPermissionsService
      .getSettingsMembersPermissions(memberEmail, accountDnvId)
      .pipe(
        tap((data: SettingsMembersPermissionsDto) => {
          ctx.dispatch(new LoadMembersPermissionsSuccess(data));
        }),
        catchError((err) => {
          ctx.patchState({
            addEditUserPermission: {
              isLoading: false,
              error: err.message,
            },
          });

          this.messageService.add({
            severity: 'error',
            summary: this.ts.translate('toast.somethingWentWrong'),
            icon: ToastSeverityIcons.ExclamationCircle,
          });

          return EMPTY;
        }),
      );
  }

  @Action(LoadMembersPermissionsSuccess)
  loadMembersPermissionsSuccess(
    ctx: StateContext<SettingsStateModel>,
    { permissionsData }: LoadMembersPermissionsSuccess,
  ) {
    ctx.patchState({
      permissionsData,
      addEditUserPermission: {
        isLoading: false,
        error: null,
      },
    });
    const { hasReceivedAdminPermissions } = ctx.getState();
    const companies =
      SettingsMembersMapper.mapToMemberCompanies(permissionsData);

    ctx.dispatch(new LoadMembersPermissionsCompanies(companies));

    ctx.dispatch(
      new UpdateMemberAreasPermissions(
        hasReceivedAdminPermissions
          ? this.getAdminMemberPermissions()
          : this.getMemberPermissions(),
      ),
    );
  }

  @Action(LoadMembersPermissionsCompanies)
  LoadMembersPermissionsCompanies(
    ctx: StateContext<SettingsStateModel>,
    { companies }: LoadMembersPermissionsCompanies,
  ) {
    ctx.patchState({ companies });

    const selectedCompanies = this.getCheckedNodes(companies);

    if (selectedCompanies.length > 0) {
      ctx.dispatch(
        new SaveMemberPermissionsCompanies(
          selectedCompanies.map((c) => c.data),
        ),
      );
    }
  }

  @Action(LoadMembersPermissionsServices)
  LoadMembersPermissionsServices(
    ctx: StateContext<SettingsStateModel>,
    { services }: LoadMembersPermissionsServices,
  ) {
    ctx.patchState({ services });

    const selectedServices = this.getCheckedNodes(services);

    if (selectedServices.length > 0) {
      ctx.dispatch(
        new SaveMemberPermissionsServices(selectedServices.map((c) => c.data)),
      );
    }
  }

  @Action(LoadMembersPermissionsSites)
  loadMembersPermissionsSites(
    ctx: StateContext<SettingsStateModel>,
    { sites }: LoadMembersPermissionsSites,
  ) {
    ctx.patchState({ sites });

    const selectedSites = this.getCheckedNodes(sites);

    if (selectedSites.length > 0) {
      ctx.dispatch(
        new SaveMemberPermissionsSites(selectedSites.map((c) => c.data)),
      );
    }
  }

  @Action(GenerateMemberPermissionsServicesOptions)
  generateMemberPermissionsServicesOptions(
    ctx: StateContext<SettingsStateModel>,
    { selectedCompanyIds }: GenerateMemberPermissionsServicesOptions,
  ) {
    const { hasReceivedAdminPermissions } = ctx.getState();

    if (hasReceivedAdminPermissions) {
      return EMPTY;
    }

    const hasSelectedCompanies = selectedCompanyIds.length > 0;

    ctx.dispatch(
      new SwitchMemberPermissionsServicesDropdownAccess(!hasSelectedCompanies),
    );

    if (!hasSelectedCompanies) {
      return ctx.dispatch([
        new LoadMembersPermissionsServices([]),
        new SaveMemberPermissionsServices([]),
      ]);
    }

    const { selectedServiceIds, permissionsData } = ctx.getState();
    const services = SettingsMembersMapper.mapToMemberServices(
      permissionsData!,
      selectedCompanyIds,
    );

    if (selectedServiceIds?.length) {
      const selectedServiceIdsAfterUpdate = this.selectedIdsAfterUpdate(
        services,
        selectedServiceIds,
      );

      if (selectedServiceIdsAfterUpdate.length !== selectedServiceIds.length) {
        ctx.dispatch(
          new SaveMemberPermissionsServices(
            selectedServiceIdsAfterUpdate as number[],
          ),
        );
      }
    }

    return ctx.dispatch(new LoadMembersPermissionsServices([...services]));
  }

  @Action(GenerateMemberPermissionsSitesOptions)
  generateMemberPermissionsSitesOptions(
    ctx: StateContext<SettingsStateModel>,
    { selectedServiceIds }: GenerateMemberPermissionsSitesOptions,
  ) {
    const hasSelectedServices = selectedServiceIds.length > 0;

    ctx.dispatch(
      new SwitchMemberPermissionsSitesDropdownAccess(!hasSelectedServices),
    );

    if (!hasSelectedServices) {
      return ctx.dispatch([
        new LoadMembersPermissionsSites([]),
        new SaveMemberPermissionsSites([]),
      ]);
    }

    const { selectedSiteIds, permissionsData, selectedCompanyIds } =
      ctx.getState();
    const nicknameBySiteId =
      this.globalSiteMasterStoreService.siteNicknameBySiteId();

    const sites = SettingsMembersMapper.mapToMemberSites(
      permissionsData!,
      selectedServiceIds,
      selectedCompanyIds!,
      nicknameBySiteId,
    );

    if (selectedSiteIds?.length) {
      const selectedSiteIdsAfterUpdate = this.selectedIdsAfterUpdate(
        sites,
        selectedServiceIds,
      );

      if (selectedSiteIdsAfterUpdate.length !== selectedSiteIds.length) {
        ctx.dispatch(
          new SaveMemberPermissionsSites(selectedSiteIdsAfterUpdate),
        );
      }
    }

    return ctx.dispatch(new LoadMembersPermissionsSites([...sites]));
  }

  @Action(SaveMemberPermissionsCompanies)
  saveMemberPermissionsCompanies(
    ctx: StateContext<SettingsStateModel>,
    { selectedCompanyIds }: SaveMemberPermissionsCompanies,
  ) {
    ctx.patchState({ selectedCompanyIds });

    ctx.dispatch(
      new GenerateMemberPermissionsServicesOptions(selectedCompanyIds),
    );
  }

  @Action(SaveMemberPermissionsServices)
  saveMemberPermissionsServices(
    ctx: StateContext<SettingsStateModel>,
    { selectedServiceIds }: SaveMemberPermissionsServices,
  ) {
    ctx.patchState({ selectedServiceIds });

    ctx.dispatch(new GenerateMemberPermissionsSitesOptions(selectedServiceIds));
  }

  @Action(SaveMemberPermissionsSites)
  saveMemberPermissionsSites(
    ctx: StateContext<SettingsStateModel>,
    { selectedSiteIds }: SaveMemberPermissionsSites,
  ) {
    ctx.patchState({ selectedSiteIds: selectedSiteIds || [] });
  }

  @Action(SwitchMemberPermissionsServicesDropdownAccess)
  switchMemberPermissionsServicesDropdownAccess(
    ctx: StateContext<SettingsStateModel>,
    { servicesDropdownDisabled }: SwitchMemberPermissionsServicesDropdownAccess,
  ) {
    ctx.patchState({ servicesDropdownDisabled });
  }

  @Action(SwitchMemberPermissionsSitesDropdownAccess)
  switchMemberPermissionsSitesDropdownAccess(
    ctx: StateContext<SettingsStateModel>,
    { sitesDropdownDisabled }: SwitchMemberPermissionsSitesDropdownAccess,
  ) {
    ctx.patchState({ sitesDropdownDisabled });
  }

  @Action(SwitchHasMemberAdminPermissions)
  switchHasMemberAdminPermissions(
    ctx: StateContext<SettingsStateModel>,
    { hasReceivedAdminPermissions }: SwitchHasMemberAdminPermissions,
  ) {
    ctx.patchState({ hasReceivedAdminPermissions });

    if (hasReceivedAdminPermissions) {
      this.handleAdminPermissions(ctx);
    } else {
      this.handleMemberPermissions(ctx);
    }
  }

  @Action(UpdateMemberAreasPermissions)
  updateMemberAreasPermissions(
    ctx: StateContext<SettingsStateModel>,
    { memberAreasPermissions }: UpdateMemberAreasPermissions,
  ) {
    ctx.patchState({ memberAreasPermissions });
  }

  @Action(DiscardNewMemberFormInfo)
  discardNewMemberFormInfo(ctx: StateContext<SettingsStateModel>) {
    ctx.patchState({
      isAddMemberFormValid: false,
      newMemberForm: null,
    });

    ctx.dispatch(new DiscardMemberPermissionsUserSelection());
  }

  @Action(DiscardMemberPermissionsDataAndCompanies)
  discardMemberPermissionsDataAndCompanies(
    ctx: StateContext<SettingsStateModel>,
  ) {
    ctx.patchState({
      permissionsData: null,
      companies: [],
    });
  }

  @Action(DiscardMemberPermissionsUserSelection)
  discardMemberPermissionsUserSelection(ctx: StateContext<SettingsStateModel>) {
    ctx.patchState({
      sites: [],
      services: [],
      companies: [],
      hasReceivedAdminPermissions: true,
      sitesDropdownDisabled: true,
      servicesDropdownDisabled: true,
      selectedCompanyIds: [],
      selectedServiceIds: [],
      selectedSiteIds: [],
      memberAreasPermissions: this.getAdminMemberPermissions(),
      addEditUserPermission: {
        isLoading: false,
        error: null,
      },
    });
  }

  @Action(SubmitNewMemberInfo)
  submitNewMemberInfo(ctx: StateContext<SettingsStateModel>) {
    const newMemberData = this.prepareNewMemberData(ctx.getState());

    return this.settingsMembersPermissionsService
      .createNewMember(newMemberData)
      .pipe(
        tap(() => {
          ctx.dispatch(new SubmitNewMemberInfoSuccess());
          ctx.dispatch(new DiscardNewMemberFormInfo());
          ctx.dispatch(new DiscardMemberPermissionsUserSelection());
          ctx.dispatch(new DiscardMemberPermissionsDataAndCompanies());
        }),
        catchError(() => {
          ctx.dispatch(new SubmitNewMemberInfoError());

          return EMPTY;
        }),
      );
  }

  @Action(SubmitNewMemberInfoSuccess)
  submitNewMemberInfoSuccess(ctx: StateContext<SettingsStateModel>) {
    const message = getToastContentBySeverity(ToastSeverity.Success);
    message.summary = this.ts.translate('dataSubmitSuccess');
    this.messageService.add(message);

    ctx.dispatch([new LoadSettingsMembersList(), new LoadSettingsAdminList()]);
  }

  @Action(SubmitNewMemberInfoError)
  submitNewMemberInfoError() {
    this.messageService.add({
      severity: 'error',
      summary: this.ts.translate('toast.somethingWentWrong'),
      icon: ToastSeverityIcons.ExclamationCircle,
    });
  }

  @Action(RemoveMember)
  removeMember(ctx: StateContext<SettingsStateModel>, { email }: RemoveMember) {
    const state = ctx.getState();
    const { selectedCoBrowsingCompanyId } = state;

    return this.settingsMembersPermissionsService
      .removeMember(email, selectedCoBrowsingCompanyId)
      .pipe(
        tap(() => {
          ctx.dispatch(new RemoveMemberSuccess());
        }),
        catchError(() => {
          ctx.dispatch(new RemoveMemberError());

          return EMPTY;
        }),
      );
  }

  @Action(RemoveMemberSuccess)
  removeMemberSuccess(ctx: StateContext<SettingsStateModel>) {
    const message = getToastContentBySeverity(ToastSeverity.Success);
    message.summary = this.ts.translate('success');
    this.messageService.add(message);
    ctx.dispatch([new LoadSettingsMembersList(), new LoadSettingsAdminList()]);
  }

  @Action(SendWelcomeEmail)
  sendWelcomeEmail(
    ctx: StateContext<SettingsStateModel>,
    { email }: SendWelcomeEmail,
  ) {
    return this.settingsMembersPermissionsService.sendWelcomeEmail(email).pipe(
      tap(() => {
        ctx.dispatch(new SendWelcomeEmailSuccess());
      }),
      catchError(() => {
        ctx.dispatch(new SendWelcomeEmailError());

        return EMPTY;
      }),
    );
  }

  @Action(SendWelcomeEmailSuccess)
  sendWelcomeEmailSuccess(ctx: StateContext<SettingsStateModel>) {
    const message = getToastContentBySeverity(ToastSeverity.Success);
    message.summary = this.ts.translate(
      'settings.membersTab.sendWelcomeEmailSuccess',
    );
    this.messageService.add(message);
    ctx.dispatch([new LoadSettingsMembersList(), new LoadSettingsAdminList()]);
  }

  @Action(SendWelcomeEmailError)
  sendWelcomeEmailError() {
    this.messageService.add({
      severity: 'error',
      summary: this.ts.translate('settings.membersTab.sendWelcomeEmailError'),
      icon: ToastSeverityIcons.ExclamationCircle,
    });
  }

  @Action(RemoveMemberError)
  removeMemberError() {
    this.messageService.add({
      severity: 'error',
      summary: this.ts.translate('toast.somethingWentWrong'),
      icon: ToastSeverityIcons.ExclamationCircle,
    });
  }

  @Action(SubmitManageMembersPermissions)
  submitManageMembersPermissions(ctx: StateContext<SettingsStateModel>) {
    const memberPermissionsData = this.prepareManangeMembersPermissions(
      ctx.getState(),
    );

    return this.settingsUserDetailsToManagePermission
      .submitManageMembersPermissions(memberPermissionsData)
      .pipe(
        tap(() => {
          ctx.dispatch(new SubmitManageMembersPermissionsSuccess());

          ctx.dispatch([
            new DiscardMemberPermissionsUserSelection(),
            new DiscardMemberPermissionsDataAndCompanies(),
            new LoadSettingsMembersList(),
            new LoadSettingsAdminList(),
          ]);
        }),
        catchError(() => {
          ctx.dispatch(new SubmitManageMembersPermissionsError());

          return EMPTY;
        }),
      );
  }

  @Action(SubmitManageMembersPermissionsSuccess)
  submitManageMembersPermissionsSuccess() {
    const message = getToastContentBySeverity(ToastSeverity.Success);
    message.summary = this.ts.translate('dataSubmitSuccess');
    this.messageService.add(message);
  }

  @Action(SubmitManageMembersPermissionsError)
  SubmitManageMembersPermissionsError() {
    this.messageService.add({
      severity: 'error',
      summary: this.ts.translate('toast.somethingWentWrong'),
      icon: ToastSeverityIcons.ExclamationCircle,
    });
  }

  @Action(ChangeEditManagePermissionsPristineForm)
  changeEditManagePermissionsPristineForm(
    ctx: StateContext<SettingsStateModel>,
    { isPristine }: ChangeEditManagePermissionsPristineForm,
  ) {
    ctx.patchState({ isEditManagePermissionsFormPristine: isPristine });
  }

  // #endregion SettingsMembersPermissions

  // #region ProfileSection

  @Action(LoadProfileData)
  loadProfileData(ctx: StateContext<SettingsStateModel>) {
    return this.profileService.getProfileData().pipe(
      map((profileDto) => {
        if (!profileDto || !profileDto.isSuccess) {
          const errorMessage =
            profileDto?.message || 'Failed to load profile data';
          const currentState = ctx.getState();
          ctx.patchState({
            loadingErrors: {
              ...currentState.loadingErrors,
              profileData: errorMessage,
            },
            loadedStates: {
              ...currentState.loadedStates,
              profileData: true,
            },
          });

          return profileDto;
        }

        return profileDto;
      }),
      filter((profileDto) => profileDto?.isSuccess ?? false),
      tap((profileDto) => {
        const currentState = ctx.getState();
        const currentProfileInformation = { ...currentState.information };
        const profileData = ProfileMapperService.mapToProfileItemModel(
          profileDto!,
        );
        const selectedCommunicationLanguage =
          profileDto?.data?.communicationLanguage ??
          currentProfileInformation.communicationLanguage ??
          ENGLISH_LANGUAGE_CODE;
        const selectedPortalLanguage =
          profileData?.information?.portalLanguage ?? ENGLISH_LANGUAGE_CODE;
        this.translocoService.setActiveLang(selectedPortalLanguage);

        ctx.patchState({
          information: {
            ...currentProfileInformation,
            ...profileData?.information,
            portalLanguage: selectedPortalLanguage,
            communicationLanguage: selectedCommunicationLanguage,
          },
          loadedStates: {
            ...currentState.loadedStates,
            profileData: true,
            profileLanguage: true,
          },
          loadingErrors: {
            ...currentState.loadingErrors,
            profileData: null,
          },
        });
      }),
    );
  }

  @Action(UpdateUserLastLoginTime)
  updateUserLastLoginTime(
    ctx: StateContext<SettingsStateModel>,
    { userLastLoginTime }: UpdateUserLastLoginTime,
  ) {
    return this.profileService.updateLoginTime(userLastLoginTime).pipe(
      map((res) => res.updateLoginTime),
      tap((result) => {
        if (!result || !result.isSuccess) {
          throw new Error(result?.message || 'Failed to update login time');
        }
        ctx.dispatch(new SetUserLastLoginTime(userLastLoginTime));
      }),
      catchError((error) => {
        // eslint-disable-next-line no-console
        console.error('Error updating last login time:', error);

        return EMPTY;
      }),
    );
  }

  @Action(UpdateCommunicationLanguageSuccess)
  updateCommunicationLanguageSuccess(
    ctx: StateContext<SettingsStateModel>,
    { communicationLanguage }: UpdateCommunicationLanguageSuccess,
  ) {
    if (!communicationLanguage) {
      return;
    }

    const state = ctx.getState();
    const currentProfileInformation = { ...state.information };

    ctx.patchState({
      information: {
        ...currentProfileInformation,
        communicationLanguage,
      },
    });
  }

  @Action(UpdateCommunicationLanguageError)
  updateCommunicationLanguageError() {
    this.messageService.add({
      severity: 'error',
      summary: this.ts.translate('toast.somethingWentWrong'),
      icon: ToastSeverityIcons.ExclamationCircle,
    });
  }

  @Action(UpdateCommunicationLanguage)
  updateCommunicationLanguage(
    ctx: StateContext<SettingsStateModel>,
    { communicationLanguage }: UpdateCommunicationLanguage,
  ) {
    return this.settingsCommunicationLanguageService
      .updateCommunicationLanguage(communicationLanguage)
      .pipe(
        tap((dto) => {
          ctx.dispatch(
            new UpdateCommunicationLanguageSuccess(
              dto.languageCode ?? communicationLanguage,
            ),
          );
        }),
        catchError(() => {
          ctx.dispatch(new UpdateCommunicationLanguageError());

          return EMPTY;
        }),
      );
  }

  @Action(UpdateSubmitSettingsStateValues)
  updateSubmitSettingsStateValues(
    ctx: StateContext<SettingsStateModel>,
    { submitSettingsValues }: UpdateSubmitSettingsStateValues,
  ) {
    const state = ctx.getState();

    const jobTitle = submitSettingsValues?.jobTitle;

    const userSelection: ProfileSettingsSelection = {
      ...state.userSelection,
      jobTitle,
    };

    ctx.patchState({ userSelection });
  }

  @Action(UpdateSubmitSettingsValues)
  updateSubmitSettingsValues(ctx: StateContext<SettingsStateModel>) {
    const state = ctx.getState();

    return this.profileService
      .updateProfileSettingsData(state.userSelection?.jobTitle)
      .pipe(
        tap((updatedProfile: any) => {
          const jobTitle =
            updatedProfile?.jobTitle ?? state.information?.jobTitle;

          const profileInformation: ProfileInformationModel = {
            ...state.information,
            jobTitle,
          };

          ctx.patchState({ information: profileInformation });
          ctx.dispatch(new LoadProfileData());
        }),
      );
  }

  @Action(UpdateSubmitSettingsStatus)
  updateSubmitSettingsStatus(
    ctx: StateContext<SettingsStateModel>,
    { submitSettingsStatus }: UpdateSubmitSettingsStatus,
  ) {
    ctx.patchState({
      submitSettingsStatus,
    });
  }

  @Action(SetProfileLanguage)
  setProfileLanguage(
    ctx: StateContext<SettingsStateModel>,
    { portalLanguage }: SetProfileLanguage,
  ) {
    const lang = portalLanguage as Language;
    this.localeService.setLocale(lang);
    this.translocoService.setActiveLang(lang);
    ctx.patchState({
      information: {
        ...ctx.getState().information,
        portalLanguage: lang,
      },
      loadedStates: {
        ...ctx.getState().loadedStates,
        profileLanguage: true,
      },
      loadingErrors: {
        ...ctx.getState().loadingErrors,
        profileLanguage: null,
      },
    });
  }

  @Action(UpdateProfileLanguageError)
  updateProfileLanguageError(ctx: StateContext<SettingsStateModel>) {
    ctx.patchState({
      loadingErrors: {
        ...ctx.getState().loadingErrors,
        profileLanguage: 'Failed to update profile language',
      },
    });
    this.messageService.add({
      severity: 'error',
      summary: this.ts.translate('toast.somethingWentWrong'),
      icon: ToastSeverityIcons.ExclamationCircle,
    });
  }

  @Action(UpdateProfileLanguage)
  updateProfileLanguage(
    ctx: StateContext<SettingsStateModel>,
    { portalLanguage }: UpdateProfileLanguage,
  ) {
    return this.profileLanguageService
      .updateProfileLanguage(portalLanguage)
      .pipe(
        tap((dto: ProfileLanguageDto) => {
          ctx.dispatch(
            new UpdateProfileLanguageSuccess(
              ProfileLanguageMapperService.mapToProfileLanguageModel(dto),
            ),
          );
          ctx.dispatch(new LoadProfileData());
        }),
        catchError(() => {
          ctx.dispatch(new UpdateProfileLanguageError());

          return EMPTY;
        }),
      );
  }

  @Action(UpdateProfileLanguageSuccess)
  updateProfileLanguageSuccess(
    ctx: StateContext<SettingsStateModel>,
    { portalLanguage }: UpdateProfileLanguageSuccess,
  ) {
    this.localeService.setLocale(portalLanguage);
    ctx.patchState({
      information: {
        ...ctx.getState().information,
        portalLanguage,
      },
    });
  }

  @Action(LoadUserRoles)
  LoadUserRoles(ctx: StateContext<SettingsStateModel>) {
    return this.settingsMembersRolesService.getSettingsMembersRoles().pipe(
      tap((data: SettingsMembersRolesDto) => {
        if (data.isSuccess) {
          ctx.dispatch(new LoadUserRolesSuccess(data.data));
        }
      }),
    );
  }

  @Action(LoadUserRolesSuccess)
  LoadUserRolesSuccess(
    ctx: StateContext<SettingsStateModel>,
    { roles }: LoadUserRolesSuccess,
  ) {
    ctx.patchState({ userRoles: roles });
  }

  // #endregion ProfileSection
  // #region SettingsCompanyDetails

  @Action(LoadSettingsCompanyDetails)
  loadSettingsCompanyDetails(ctx: StateContext<SettingsStateModel>) {
    ctx.patchState({
      loadedStates: {
        ...ctx.getState().loadedStates,
        companyDetails: false,
      },
      loadingErrors: {
        ...ctx.getState().loadingErrors,
        companyDetails: null,
      },
    });

    return this.settingsCompanyDetailsService.getSettingsCompanyDetails().pipe(
      tap((companyDto) => {
        ctx.dispatch(
          new LoadSettingsCompanyDetailsSuccess(
            SettingsCompanyDetailsMapperService.mapToSettingsCompanyDetailsModel(
              companyDto?.data ?? {
                isAdmin: false,
                legalEntities: [],
                parentCompany: null,
                userStatus: '',
              },
            ),
          ),
        );
        ctx.dispatch(new UpdateSettingsCompanyDetailsEntityListFilterOptions());
      }),
      catchError((error) => {
        const errorMessage = error?.message || 'Failed to load company details';
        ctx.dispatch(new LoadSettingsCompanyDetailsFail(errorMessage));

        return of(null);
      }),
    );
  }

  @Action(LoadSettingsCompanyDetailsSuccess)
  loadSettingsCompanyDetailsSuccess(
    ctx: StateContext<SettingsStateModel>,
    { companyDetails }: LoadSettingsCompanyDetailsSuccess,
  ) {
    const { isUserAdmin, legalEntityList, parentCompany } = companyDetails;

    ctx.patchState({
      isUserAdmin,
      legalEntityList,
      legalEntityListCounter: legalEntityList.length,
      parentCompany,
      loadedStates: {
        ...ctx.getState().loadedStates,
        companyDetails: true,
      },
    });
  }

  @Action(LoadSettingsCompanyDetailsFail)
  loadSettingsCompanyDetailsFail(
    ctx: StateContext<SettingsStateModel>,
    { errorMessage }: LoadSettingsCompanyDetailsFail,
  ) {
    ctx.patchState({
      loadingErrors: {
        ...ctx.getState().loadingErrors,
        companyDetails: errorMessage,
      },
      loadedStates: {
        ...ctx.getState().loadedStates,
        companyDetails: true,
      },
    });
  }

  @Action(SetCompanyDetailsAdminStatus)
  setCompanyDetailsAdminStatus(
    ctx: StateContext<SettingsStateModel>,
    { isUserAdmin }: SetCompanyDetailsAdminStatus,
  ) {
    ctx.patchState({
      isUserAdmin,
    });
  }

  @Action(LoadSettingsCompanyDetailsCountryList)
  loadSettingsCompanyDetailsCountryList(ctx: StateContext<SettingsStateModel>) {
    return this.getSettingsCompanyDetailsCountryList().pipe(
      tap((data) => {
        ctx.dispatch(
          new LoadSettingsCompanyDetailsCountryListSuccess(
            SettingsCompanyDetailsMapperService.mapToSettingsCompanyDetailsCountryListModel(
              data,
            ),
          ),
        );
        ctx.dispatch(new UpdateSettingsCompanyDetailsEntityListFilterOptions());
      }),
    );
  }

  @Action(LoadSettingsCompanyDetailsCountryListSuccess)
  loadSettingsCompanyDetailsCountryListSuccess(
    ctx: StateContext<SettingsStateModel>,
    { data }: LoadSettingsCompanyDetailsCountryListSuccess,
  ) {
    const { countryList, countryActiveId } = data;

    ctx.patchState({ countryList, countryActiveId });
  }

  @Action(UpdateSettingsCompanyDetails)
  updateSettingsCompanyDetails(
    ctx: StateContext<SettingsStateModel>,
    { params }: UpdateSettingsCompanyDetails,
  ) {
    return this.editSettingsCompanyDetails(params);
  }

  @Action(UpdateSettingsCompanyDetailsEntityListFilterOptions)
  updateSettingsCompanyDetailsEntityListFilterOptions(
    ctx: StateContext<SettingsStateModel>,
  ) {
    const { legalEntityList, legalEntityGridConfig } = ctx.getState();

    const legalEntityFilterOptions = getFilterOptions(
      legalEntityList,
      legalEntityGridConfig,
      COMPANY_DETAILS_LEGAL_ENTITIES_COLUMNS_DEFINITION,
    );

    ctx.patchState({ legalEntityFilterOptions });
  }

  @Action(UpdateSettingsCompanyDetailsEntityListGridConfig)
  updateSettingsCompanyDetailsEntityListGridConfig(
    ctx: StateContext<SettingsStateModel>,
    { legalEntityGridConfig }: UpdateSettingsCompanyDetailsEntityListGridConfig,
  ) {
    ctx.patchState({ legalEntityGridConfig });
    ctx.dispatch(new UpdateSettingsCompanyDetailsEntityListFilterOptions());
  }

  @Action(ResetSettingsCompanyDetailsState)
  resetSettingsCompanyDetailsState(
    ctx: StateContext<SettingsStateModel>,
  ): void {
    ctx.patchState({
      legalEntityList: [],
      legalEntityGridConfig: DEFAULT_GRID_CONFIG,
      legalEntityFilterOptions: {},
    });
  }

  @Action(UpdateEditCompanyDetailsFormValidity)
  updateEditCompanyDetailsFormValidity(
    ctx: StateContext<SettingsStateModel>,
    { isEditCompanyDetailsFormValid }: UpdateEditCompanyDetailsFormValidity,
  ) {
    ctx.patchState({
      isEditCompanyDetailsFormValid,
    });
  }

  // #endregion SettingsCompanyDetails

  // #region AdminCoBrowsing

  @Action(UpdateDnvUserAccessType)
  updateDnvUserAccessType(
    ctx: StateContext<SettingsStateModel>,
    { dnvUserAccessType }: UpdateDnvUserAccessType,
  ) {
    const state = ctx.getState();

    ctx.patchState({
      information: {
        ...state.information,
        accessType: dnvUserAccessType,
      },
    });
  }

  @Action(UpdateIsDnvUser)
  updateIsDnvUser(
    ctx: StateContext<SettingsStateModel>,
    { isDnvUser }: UpdateIsDnvUser,
  ) {
    ctx.patchState({
      isDnvUser,
    });
  }

  @Action(GetCompanySuggestions)
  getCompanySuggestions(
    ctx: StateContext<SettingsStateModel>,
    { searchText }: GetCompanySuggestions,
  ) {
    if (!searchText || searchText.trim().length === 0) {
      ctx.patchState({
        companySuggestion: {
          ...ctx.getState().companySuggestion,
          suggestionList: [],
          isLoading: false,
          error: '',
        },
      });

      return EMPTY;
    }

    ctx.patchState({
      companySuggestion: {
        ...ctx.getState().companySuggestion,
        isLoading: true,
        error: '',
      },
    });

    return this.coBrowsingCompanyListService
      .getCompanySuggestions(searchText.trim())
      .pipe(
        tap((data: CoBrowsingCompanyListDto) => {
          const suggestions =
            CoBrowsingMapperService.mapToCoBrowsingCompanyList(data.data);
          ctx.dispatch(new GetCompanySuggestionsSuccess(suggestions));
        }),
        catchError((error) => {
          ctx.dispatch(new GetCompanySuggestionsError(error));

          return EMPTY;
        }),
      );
  }

  @Action(GetCompanySuggestionsSuccess)
  getCompanySuggestionsSuccess(
    ctx: StateContext<SettingsStateModel>,
    { suggestions }: GetCompanySuggestionsSuccess,
  ) {
    ctx.patchState({
      companySuggestion: {
        ...ctx.getState().companySuggestion,
        suggestionList: suggestions,
        isLoading: false,
      },
    });
  }

  @Action(GetCompanySuggestionsError)
  getCompanySuggestionsError(
    ctx: StateContext<SettingsStateModel>,
    { error }: GetCompanySuggestionsError,
  ) {
    ctx.patchState({
      companySuggestion: {
        ...ctx.getState().companySuggestion,
        isLoading: false,
        error: error?.message || 'Failed to load company suggestions',
      },
    });
  }

  @Action(UpdateSelectedCobrowsingCompany)
  updateSelectedCobrowsingCompany(
    ctx: StateContext<SettingsStateModel>,
    { selectedCompany }: UpdateSelectedCobrowsingCompany,
  ) {
    ctx.patchState({
      selectedCoBrowsingCompany: selectedCompany,
      selectedCoBrowsingCompanyId: selectedCompany?.id || null,
    });
  }

  @Action(ResetSelectedCobrowsingCompany)
  resetSelectedCobrowsingCompany(ctx: StateContext<SettingsStateModel>) {
    ctx.patchState({
      selectedCoBrowsingCompany: null,
      selectedCoBrowsingCompanyId: null,
    });
  }

  @Action(ResetProfileLoadAndErrorState)
  resetProfileLoadAndErrorState(ctx: StateContext<SettingsStateModel>): void {
    ctx.patchState({
      loadedStates: {
        ...ctx.getState().loadedStates,
        profileData: false,
        profileLanguage: false,
      },
      loadingErrors: {
        ...ctx.getState().loadingErrors,
        profileData: null,
        profileLanguage: null,
      },
      information: {
        firstName: '',
        lastName: '',
        country: '',
        countryCode: '',
        region: '',
        email: '',
        phone: '',
        portalLanguage: ENGLISH_LANGUAGE_CODE,
        veracityId: '',
        communicationLanguage: ENGLISH_LANGUAGE_CODE,
        title: '',
        jobTitle: '',
        oscContactId: '',
        accessLevel: {},
        sidebarMenu: [],
      },
    });
  }

  @Action(ResetCompanyLoadAndErrorState)
  resetCompanyLoadAndErrorState(ctx: StateContext<SettingsStateModel>): void {
    ctx.patchState({
      loadedStates: {
        ...ctx.getState().loadedStates,
        companyDetails: false,
      },
      loadingErrors: {
        ...ctx.getState().loadingErrors,
        companyDetails: null,
      },
      isUserAdmin: false,
      legalEntityList: [],
      legalEntityListCounter: 0,
      parentCompany: null,
    });
  }

  @Action(UpdateImpersonatedUser)
  updateImpersonatedUser(
    ctx: StateContext<SettingsStateModel>,
    { impersonatedUserEmail }: UpdateImpersonatedUser,
  ) {
    return this.coBrowsingCookieService.postUserEmailCookie(
      impersonatedUserEmail,
    );
  }

  // #endregion AdminCoBrowsing

  // #region Helper methods

  private getSettingsCompanyDetailsCountryList(): Observable<
    SettingsCompanyDetailsCountryListDataDto[]
  > {
    return this.settingsCompanyDetailsService
      .getSettingsCompanyDetailsCountryList()
      .pipe(
        map((dto: SettingsCompanyDetailsCountryListDto) =>
          dto?.isSuccess && dto?.data
            ? dto.data
            : ([] as SettingsCompanyDetailsCountryListDataDto[]),
        ),
      );
  }

  private editSettingsCompanyDetails(params: any) {
    return this.settingsCompanyDetailsService.editSettingsCompanyDetails({
      ...params,
    });
  }

  private handleAdminPermissions(ctx: StateContext<SettingsStateModel>): void {
    ctx.dispatch([
      new LoadMembersPermissionsServices([]),
      new SwitchMemberPermissionsServicesDropdownAccess(true),
      new SaveMemberPermissionsServices([]),
      new SaveMemberPermissionsSites([]),
      new UpdateMemberAreasPermissions(this.getAdminMemberPermissions()),
    ]);
  }

  private handleMemberPermissions(ctx: StateContext<SettingsStateModel>): void {
    ctx.dispatch(new UpdateMemberAreasPermissions(this.getMemberPermissions()));

    const { selectedCompanyIds, selectedServiceIds } = ctx.getState();

    if (selectedCompanyIds?.length) {
      ctx.dispatch(
        new GenerateMemberPermissionsServicesOptions(selectedCompanyIds),
      );
    }

    if (selectedServiceIds?.length) {
      ctx.dispatch(new SwitchMemberPermissionsSitesDropdownAccess(false));
    }
  }

  private getAdminMemberPermissions(): MemberAreasPermissions[] {
    return [
      {
        area: MemberAreaPermissions.Contracts,
        permission: {
          view: {
            isChecked: true,
            roleId: 1,
            roleName: AccessAreaRoleNames.CONTRACTS_VIEW,
          },
          edit: undefined,
          submit: undefined,
        },
      },
      {
        area: MemberAreaPermissions.Schedules,
        permission: {
          view: {
            isChecked: true,
            roleId: 2,
            roleName: AccessAreaRoleNames.SCHEDULE_VIEW,
          },
          edit: {
            isChecked: true,
            roleId: 3,
            roleName: AccessAreaRoleNames.SCHEDULE_EDIT,
          },
        },
      },
      {
        area: MemberAreaPermissions.Audits,
        permission: {
          view: {
            isChecked: true,
            roleId: 8,
            roleName: AccessAreaRoleNames.AUDITS_VIEW,
          },
          edit: {
            isChecked: true,
            roleId: 9,
            roleName: AccessAreaRoleNames.AUDITS_EDIT,
          },
        },
      },
      {
        area: MemberAreaPermissions.Findings,
        permission: {
          view: {
            isChecked: true,
            roleId: 10,
            roleName: AccessAreaRoleNames.FINDINGS_VIEW,
          },
          edit: {
            isChecked: true,
            roleId: 11,
            roleName: AccessAreaRoleNames.FINDINGS_EDIT,
          },
          submit: {
            isChecked: true,
            roleId: 12,
            roleName: AccessAreaRoleNames.FINDINGS_SUBMIT,
          },
        },
      },
      {
        area: MemberAreaPermissions.Certificates,
        permission: {
          view: {
            isChecked: true,
            roleId: 4,
            roleName: AccessAreaRoleNames.CERTIFICATES_VIEW,
          },
          edit: {
            isChecked: true,
            roleId: 5,
            roleName: AccessAreaRoleNames.CERTIFICATES_EDIT,
          },
        },
      },
      {
        area: MemberAreaPermissions.Financials,
        permission: {
          view: undefined,
          edit: {
            isChecked: true,
            roleId: 6,
            roleName: AccessAreaRoleNames.FINANCIALS_EDIT,
          },
        },
      },
    ];
  }

  private getMemberPermissions(): MemberAreasPermissions[] {
    return [
      {
        area: MemberAreaPermissions.Contracts,
        permission: {
          view: {
            isChecked: false,
            roleId: 1,
            roleName: 'Contracts_View',
          },
          edit: undefined,
        },
      },
      {
        area: MemberAreaPermissions.Schedules,
        permission: {
          view: {
            isChecked: true,
            roleId: 2,
            roleName: AccessAreaRoleNames.SCHEDULE_VIEW,
          },
          edit: {
            isChecked: false,
            roleId: 3,
            roleName: AccessAreaRoleNames.SCHEDULE_EDIT,
          },
        },
      },
      {
        area: MemberAreaPermissions.Audits,
        permission: {
          view: {
            isChecked: true,
            roleId: 8,
            roleName: AccessAreaRoleNames.AUDITS_VIEW,
          },
          edit: {
            isChecked: false,
            roleId: 9,
            roleName: AccessAreaRoleNames.AUDITS_EDIT,
          },
        },
      },
      {
        area: MemberAreaPermissions.Findings,
        permission: {
          view: {
            isChecked: true,
            roleId: 10,
            roleName: AccessAreaRoleNames.FINDINGS_VIEW,
          },
          edit: {
            isChecked: false,
            roleId: 11,
            roleName: AccessAreaRoleNames.FINDINGS_EDIT,
          },
          submit: {
            isChecked: false,
            roleId: 12,
            roleName: AccessAreaRoleNames.FINDINGS_SUBMIT,
          },
        },
      },
      {
        area: MemberAreaPermissions.Certificates,
        permission: {
          view: {
            isChecked: true,
            roleId: 4,
            roleName: 'Certificates_View',
          },
          edit: {
            isChecked: false,
            roleId: 5,
            roleName: 'Certificates_Edit',
          },
        },
      },
      {
        area: MemberAreaPermissions.Financials,
        permission: {
          view: undefined,
          edit: {
            isChecked: false,
            roleId: 6,
            roleName: 'Financials_Edit',
          },
        },
      },
    ];
  }

  private prepareNewMemberData(
    state: SettingsStateModel,
  ): CreateContactRequest {
    const {
      permissionsData,
      newMemberForm,
      hasReceivedAdminPermissions,
      selectedCompanyIds,
      selectedServiceIds,
      selectedSiteIds,
      memberAreasPermissions,
      countryList,
    } = state;

    const accessAreas = this.getAccessAreas(memberAreasPermissions);
    const mobileCountryCode = newMemberForm?.countryCode
      ? (countryList.find(
          (country) => country.countryCode === newMemberForm.countryCode,
        )?.mobileCountryCode ?? '')
      : '';

    let newMemberData: CreateContactRequest = {
      email: newMemberForm!.email,
      firstName: newMemberForm!.firstName,
      lastName: newMemberForm!.lastName,
      title: newMemberForm!.title ?? '',
      communicationLanguage: newMemberForm!.communicationLanguage,
      mobileCountryCode,
      phone: newMemberForm!.phone ?? '',
      countryId: 0,
      jobTitle: newMemberForm!.role,
      isAdmin: hasReceivedAdminPermissions ? 1 : 0,
      accessAreas,
      companies: SettingsMembersMapper.mapToSelectedCompanies(
        permissionsData!,
        selectedCompanyIds!,
      ),
      services: [] as any,
      countries: [] as any,
    };

    if (!hasReceivedAdminPermissions) {
      newMemberData = {
        ...newMemberData,
        services: SettingsMembersMapper.mapToSelectedServices(
          permissionsData!,
          selectedServiceIds!,
        ),
        countries: SettingsMembersMapper.mapToSelectedSites(
          permissionsData!,
          selectedSiteIds!,
        ),
      };
    }

    return newMemberData;
  }

  private prepareManangeMembersPermissions(
    state: SettingsStateModel,
  ): ManageMembersRequestModel {
    const {
      permissionsData,
      hasReceivedAdminPermissions,
      selectedCompanyIds,
      selectedServiceIds,
      selectedSiteIds,
      memberAreasPermissions,
    } = state;

    const accessAreas = this.getAccessAreas(memberAreasPermissions);
    let manageMemberPermissionsData: ManageMembersRequestModel = {
      userId: permissionsData?.data[0]?.userId || 0,
      email: permissionsData?.data[0]?.emailId || '',
      isAdmin: hasReceivedAdminPermissions ? 1 : 0,
      accessAreas,
      companies: SettingsMembersMapper.mapToSelectedCompanies(
        permissionsData!,
        selectedCompanyIds!,
      ),
      services: [],
      countries: [],
    };

    if (!hasReceivedAdminPermissions) {
      manageMemberPermissionsData = {
        ...manageMemberPermissionsData,
        services: SettingsMembersMapper.mapToSelectedServices(
          permissionsData!,
          selectedServiceIds!,
        ),
        countries: SettingsMembersMapper.mapToSelectedSites(
          permissionsData!,
          selectedSiteIds!,
        ),
      };
    }

    return manageMemberPermissionsData;
  }

  private getAccessAreas(
    memberAreasPermissions: MemberAreasPermissions[] | undefined,
  ): CreateContactAccessArea[] {
    if (!memberAreasPermissions) {
      return [];
    }

    return memberAreasPermissions.reduce(
      (acc: CreateContactAccessArea[], { permission }) => {
        if (permission.view?.isChecked) {
          acc.push({
            roleId: permission.view.roleId,
            roleName: permission.view.roleName,
          });
        }

        if (permission.edit?.isChecked) {
          acc.push({
            roleId: permission.edit.roleId,
            roleName: permission.edit.roleName,
          });
        }

        if (permission.submit?.isChecked) {
          acc.push({
            roleId: permission.submit.roleId,
            roleName: permission.submit.roleName,
          });
        }

        return acc;
      },
      [],
    );
  }

  private selectedIdsAfterUpdate(
    treeStructure: TreeNode[],
    selectedIds: (string | number)[],
  ): (string | number)[] {
    let selectedSiteIds: (string | number)[] = [];

    treeStructure.forEach((element) => {
      if (selectedIds.includes(element.data)) {
        selectedSiteIds.push(element.data);
      }

      if (element.children) {
        selectedSiteIds = selectedSiteIds.concat(
          this.selectedIdsAfterUpdate(element.children, selectedIds),
        );
      }
    });

    return selectedSiteIds;
  }

  private getCheckedNodes(nodes: TreeNode[]): TreeNode[] {
    const checkedNodes: TreeNode[] = [];

    nodes.forEach((node) => {
      if (node.checked) {
        checkedNodes.push(node);
      }

      if (node.children && node.children.length > 0) {
        checkedNodes.push(...this.getCheckedNodes(node.children));
      }
    });

    return checkedNodes;
  }

  // #endregion Helper methods
}
